<template>
  <div>
    <template v-if="!route.meta.hideChrome">
      <notification-header
        v-for="banner in notificationStore.banners"
        :key="banner.bannerID"
        :notification="banner.message"
        :banner-id="banner.bannerID"
        :url="banner.url"
        @close-banner="closeBanner"
      />
      <header-component />
      <div v-if="canSeeRebates(userStore.user.username)" class="rebate-accent-bar">
        <router-link to="/my-rebates" class="rebate-accent-bar__link">
          <span class="rebate-accent-bar__badge">0.1% REBATE</span>
          <span class="rebate-accent-bar__text">on every matched offer</span>
        </router-link>
      </div>
    </template>
    <arb-offer-info-modal
      v-if="arbOfferInfoModalOpen"
      @close="arbOfferInfoModalOpen = false"
    />
    <slippage-info-modal
      v-if="slippageInfoModalOpen"
      @close="slippageInfoModalOpen = false"
    />
    <error v-if="error.happens" :error="error" />
    <router-view
      v-else
      @show-alert="showAlert"
      @hide-alert="alert.shown = false"
    />
    <div class="notifications-wrapper">
      <transition name="fade">
        <notification-slider
          v-if="notificationStore.priorityAlerts.length > 0"
          :notifications="notificationStore.priorityAlerts"
          @clear-matched-notifications="clearPriorityAlerts"
          @next-notification="nextPriorityAlert"
        />
      </transition>

      <transition-group name="fade">
        <notification
          v-for="(notification, nIx) in notificationStore.alerts"
          :key="`key-${nIx}`"
          :notification="notification"
          :index="nIx"
          @remove-notification="removeAlert"
        />
      </transition-group>
    </div>

    <!-- Bet Confirmation Modal -->
    <bet-confirmation-modal ref="betConfirmationModal" />
    <!-- Fill Summary Modal (shown when a fill differs from what was requested) -->
    <fill-summary-modal ref="fillSummaryModal" />
    <price-moved-modal ref="priceMovedModal" />
    <!-- Slippage Confirm Modal (pre-placement gate for materially worse fills) -->
    <slippage-confirm-modal ref="slippageConfirmModal" />
    <personal-info-modal
      v-if="showPersonalInfoModal"
      @close="showPersonalInfoModal = false"
      @completed="onPersonalInfoCompleted"
    />
    <deposit-modal v-if="showDepositModal" />
    <withdraw-modal v-if="showWithdrawModal" />
    <two-factor-enroll
      v-if="showTwoFactorEnroll"
      :source="twoFactorEnrollSource"
      @enrolled="showTwoFactorEnroll = false"
      @close-modal="showTwoFactorEnroll = false"
    />
    <login-modal v-if="showLoginModal" @close-modal="showLoginModal = false" />
    <!-- Global open-offers modal: opened via the emitter from anywhere the
         user acts on an aggregate book row holding several of their orders -->
    <open-offers-modal
      v-if="showGlobalOpenOffers"
      :game-id="globalOpenOffersGameID"
      @close-modal="showGlobalOpenOffers = false"
    />
    <footer-component v-if="!route.meta.hideChrome" />
    <veriff-modal v-if="showVeriffModal" />
    <mobile-menu v-if="!route.meta.hideChrome && userStore.user.isLoggedIn && isMobile" />
  </div>
</template>

<script setup>
import { ref, computed, onBeforeUnmount, onMounted, watch } from 'vue';
const showDepositModal = ref(false);
const showWithdrawModal = ref(false);
const showPersonalInfoModal = ref(false);
const showVeriffModal = ref(false);
const showTwoFactorEnroll = ref(false);
// Login without leaving the page — opened by checkAuthAndShowLogin() whenever a
// logged-out user tries to bet.
const showLoginModal = ref(false);
// Where the enroll modal was opened from ('account' | 'deposit' | ...) — drives
// the PostHog funnel attribution inside TwoFactorEnroll.
const twoFactorEnrollSource = ref('account');
const showGlobalOpenOffers = ref(false);
const globalOpenOffersGameID = ref('');
// Components
import NotificationHeader from '@/components/global/header/NotificationHeader.vue';
import HeaderComponent from '@/components/global/header/Header.vue';
import FooterComponent from '@/components/global/Footer.vue';
import * as Sentry from '@sentry/vue';
import SentryUtils from '@/core/SentryUtils';
import Notification from '@/components/global/Notification.vue';
import NotificationSlider from '@/components/global/MultipleNotifications.vue';
import BetConfirmationModal from '@/components/global/BetConfirmationModal.vue';
import FillSummaryModal from '@/components/global/FillSummaryModal.vue';
import PriceMovedModal from '@/components/global/PriceMovedModal.vue';
import SlippageConfirmModal from '@/components/global/SlippageConfirmModal.vue';
import DepositModal from '@/components/global/DepositModal.vue';
import PersonalInfoModal from '@/components/global/PersonalInfoModal.vue';
import WithdrawModal from '@/components/global/WithdrawModal.vue';
import TwoFactorEnroll from '@/components/account/modals/TwoFactorEnroll.vue';
import OpenOffersModal from '@/components/userBets/modals/OpenOffersModal.vue';
import MobileMenu from '@/components/global/MobileMenu.vue';
import ArbOfferInfoModal from '@/components/global/ArbOfferInfoModal.vue';
import SlippageInfoModal from '@/components/global/SlippageInfoModal.vue';
import VeriffModal from '@/components/global/VeriffModal.vue';
import LoginModal from '@/components/login/LoginModal.vue';
// store values
import { useMiscStore } from '@/stores/store';
import { useExchangeStore } from '@/stores/exchange';
import { useUserStore } from '@/stores/user';
import { useNotificationStore } from '@/stores/notification';
import { resetAllStores } from '@/core/resetAllStores';
import { emitter } from '@/core/emitter.js';
import { io } from 'socket.io-client';
import { v4 as uuidv4 } from 'uuid';
import { canSeeRebates } from '@/core/rebateUtils';

import { useAPI } from '@/composables/useAPI';
import { useTwoFactor } from '@/composables/useTwoFactor';
import { useWindowSize } from '@/composables/useWindowSize';
import { PostHogUtils } from '@/core/PostHogUtils';
const { width: screenWidth } = useWindowSize();
const { getActiveMethod: getActiveTwoFactorMethod } = useTwoFactor();

// Stamp every logged-in user's current 2FA factor as a PostHog person property
// for adoption metrics. This used to live in the (now-removed) 2FA banner, which
// ran it globally on login; keep the same coverage by calling it on mount (for
// already-logged-in page loads) and on the `logged-in` event.
async function syncTwoFactorMethod() {
  if (!userStore.user?.isLoggedIn) return;
  try {
    const method = await getActiveTwoFactorMethod();
    PostHogUtils.syncSettings({ twofa_method: method || 'none' });
  } catch {
    // Best-effort metric — never block login on a failed/absent lookup.
  }
}

import { useRoute, useRouter } from 'vue-router';

import GameExpiryWorker from '@/app-workers/gameExpiry.worker?worker';

import { useAuthStore } from '@/stores/auth';
import { WebSocketManager } from '@/core/sockets/stable-socket/StableWebsocket';
import { ProtobufWebSocketManager } from '@/core/sockets/stable-socket/ProtobufWebSocketManager';
import { subscriptionManager } from '@/core/sockets/subscriptionManager';
import { usePlaceTrackerStore } from '@/stores/placeTracker';
import { resolveLeagueSlug, userCanUseCashier } from '@/consts/constants';
import { handleUserFeedV1SocketMessage } from '@/core/sockets/handleUserFeed';
import { handlePositionUpdate } from '@/core/sockets/handlePositionUpdate';

// Feature flag for protobuf price stream
const USE_PROTOBUF_PRICE_STREAM = import.meta.env.VITE_USE_PROTOBUF_PRICE_STREAM === 'true';

const miscStore = useMiscStore();
const exchangeStore = useExchangeStore();
const placeTracker = usePlaceTrackerStore();
const userStore = useUserStore();
const authStore = useAuthStore();
const notificationStore = useNotificationStore();

// A deposit just confirmed. If the user has NO 2FA factor, this is the moment
// to offer enrollment — there's now money in the account that will eventually
// need a withdrawal, and withdrawing to a new address requires 2FA. Users with
// any factor (including legacy isTwoFA users, who resolve to 'email') are never
// interrupted. Fail closed: if the factor state can't be confirmed, don't pop.
async function promptTwoFactorEnrollAfterDeposit() {
  try {
    const method = await getActiveTwoFactorMethod();
    if (!method) {
      twoFactorEnrollSource.value = 'deposit';
      showTwoFactorEnroll.value = true;
    }
  } catch {
    // unknown state — never open the modal on a guess
  }
}

// Lazy load Intercom to improve performance
let intercomLoaded = false;
const loadIntercom = async () => {
  if (intercomLoaded) return;

  try {
    const Intercom = (await import('@intercom/messenger-js-sdk')).default;
    Intercom({
      app_id: import.meta.env.VITE_INTERCOM_ID,
      user_id: userStore.user?.id,
      name: userStore.user?.username,
      email: userStore.user?.userEmail,
      created_at: userStore.user?.createdAt,
    });
    intercomLoaded = true;
    
    // Hide the default launcher on mobile (we have Support in dropdown instead)
    if (isMobile.value && window.Intercom) {
      window.Intercom('update', { hide_default_launcher: true });
    }
  } catch {
    // Failed to load Intercom - handled silently
  }
};

// Load Intercom after initial page load
setTimeout(loadIntercom, 2000);

// Make loadIntercom available globally for manual triggering
window.loadIntercom = loadIntercom;

Sentry.addBreadcrumb({
  category: 'user',
  message: 'Current User',
  level: 'info',
  data: {
    username: userStore.user?.username,
  },
});

/**
 * Functinoality for handling notifications
 */
const timerRemoveNotificationById = ref();
const timersetGameOddsDelta = ref();

onMounted(() => {
  document.addEventListener(
    'visibilitychange',
    onVisibilityChangeReseedUserOrders,
  );
  const fetchUserData = async () => {
    if (userStore.user.id) {
      const { get: getMe } = useAPI('user/getMe/');
      const getMeResponse = await getMe();
      userStore.setGetMe(getMeResponse.user);
      const veriffData = userStore.user?.veriffData;
      if (veriffData?.required !== true || veriffData?.status === 'approved') return;

      // User is gated by KYC (verification required, not yet approved). This is
      // the top of the KYC funnel — the denominator for started/finished/result.
      PostHogUtils.markKycRequired();

      if (!veriffData?.sessionID) {
        emitter.emit('showVeriffModal');
        return;
      }

      await userStore.verifyUserKYC(veriffData.sessionID);
      if (userStore.user?.veriffData?.status !== 'approved') {
        emitter.emit('showVeriffModal');
      }
    }
  };

  fetchUserData();
  if (isMobile.value) {
    if(intercomLoaded){
      window.Intercom('update', { hide_default_launcher: true });
    }
  }
  if (userStore.user.isLoggedIn) {
    notificationStore.refreshBanners();
    notificationStore.refreshInboxNotifications();
  }
  syncTwoFactorMethod();
});

/**
 * Route Functionality
 */
const route = useRoute();
const router = useRouter();
watch(route, () => {
  error.value.happens = false;
  alert.value.shown = false;
  cancelAllGameExpiry();
});

/**
 * Per-page price stream subscription routing. Each route translates to one of:
 *   - singleGame(gameID):   /exchange-single/:gameID
 *   - sport(sport):         /:sport/all and /exchange/:sport/all (any sport
 *                           with `league=all` in the URL)
 *   - leagues(realLeagues): /:sport/:league and /exchange/:sport/:league for
 *                           real leagues, with the URL slug resolved through
 *                           `resolveLeagueSlug` so e.g. `ufc -> UFCMMA`
 *   - featured(ids):        home/lobby AND `/exchange/:sport/featured-*` (set
 *                           by the view once its REST data loads via
 *                           `subscriptionManager.setFeatured(...)`)
 *   - clear():              anything else (my-bets, account, etc.)
 *
 * The home/lobby and `featured-*` slug pages cannot resolve their game set
 * from route params alone, so we clear here and the view is responsible for
 * calling setFeatured once it has data.
 */
function applyRouteSubscription(toRoute) {
  if (!subscriptionManager.isEnabled()) return;

  const name = toRoute?.name;

  if (name === 'SingleGameMarket') {
    const gameID = toRoute.params?.gameID;
    if (gameID) {
      subscriptionManager.setSingleGame(String(gameID));
    } else {
      subscriptionManager.clear();
    }
    return;
  }

  if (name === 'GamesPage' || name === 'Exchange') {
    const sport = toRoute.params?.sport;
    const league = toRoute.params?.league;

    // /:sport/all -> sport-level subscription so the page receives every
    // update for the sport without enumerating leagues or games.
    if (league === 'all' && sport) {
      subscriptionManager.setSport(String(sport));
      return;
    }

    // /:sport/featured-* -> page-driven gameID set; the view will call
    // `setFeatured` once its REST data loads. Clear so we don't inherit a
    // stale subscription from the previous route in the meantime.
    if (typeof league === 'string' && league.startsWith('featured-')) {
      subscriptionManager.clear();
      return;
    }

    if (league) {
      subscriptionManager.setLeagues(resolveLeagueSlug(String(league)));
    } else {
      subscriptionManager.clear();
    }
    return;
  }

  if (name === 'HomePage') {
    // The Home view will replace this with a featured-game subscription as
    // soon as its REST data loads. Until then, receive nothing rather than
    // inheriting a stale subscription from the previous route.
    subscriptionManager.clear();
    return;
  }

  // AllProps spans every prop league across every sport. The set of games
  // isn't knowable from route params (no :sport/:league), so we clear here
  // and the AllProps view calls subscriptionManager.setFeatured(gameIDs)
  // once its REST data resolves — same pattern as HomePage / featured-*.
  if (
    name === 'AllProps' ||
    name === 'AllPropsExchange' ||
    name === 'AllPropsSportsbook'
  ) {
    subscriptionManager.clear();
    return;
  }

  // Any other route (account, my-bets, withdraw, etc.) — receive nothing.
  subscriptionManager.clear();
}

router.afterEach((toRoute) => {
  applyRouteSubscription(toRoute);
});

/**
 * Functionality related to game expiry
 */
const gameExpiryWorker = new GameExpiryWorker();
gameExpiryWorker.onmessage = ({ data }) => {
  switch (data.type) {
    case 'GAME_EXPIRED':
      if (data.gameId) {
        exchangeStore.removeGame({ id: data.gameId });
      }
      break;

    default:
      break;
  }
};
function terminateAppWorker() {
  gameExpiryWorker.terminate();
}
function cancelAllGameExpiry() {
  if (gameExpiryWorker) {
    gameExpiryWorker.postMessage({
      type: 'CANCEL_ALL_GAMES_EXPIRY',
    });
  }
}
function cancelGamesExpiry(gameIds = []) {
  if (gameExpiryWorker) {
    gameExpiryWorker.postMessage({
      type: 'CANCEL_GAMES_EXPIRY',
      gameIds,
    });
  }
}
function setGameExpiry(type) {
  if (gameExpiryWorker) {
    let gamesData = [];
    if (type === 'singleGame') {
      if (exchangeStore.singleGame.game.id) {
        gamesData = exchangeStore.singleGame.games.map((game) => ({
          id: game.id,
          start: game.start,
          ended: game.ended,
          live: game.live,
        }));
      }
    } else if (type === 'loggedIn') {
      gamesData = exchangeStore.topTradedOrderbooks.map((game) => ({
        id: game.id,
        start: game.start,
        ended: game.ended,
        live: game.live,
      }));
    } else {
      gamesData = exchangeStore.games.map((game) => ({
        id: game.id,
        start: game.start,
        ended: game.ended,
        live: game.live,
      }));
    }

    if (gamesData && gamesData.length) {
      // Set game expiries
      gameExpiryWorker.postMessage({
        type: 'SET_GAMES_EXPIRY',
        games: gamesData,
      });
    }
  }
}

const wsEndpoint = import.meta.env.VITE_APP_SOCKET_API_ENDPOINT;
const streamEndpoint = import.meta.env.VITE_APP_STREAMING_API_ENDPOINT;
// The rendered price feed is served by the price-feed fanout, a separate
// deployment from streaming-api. Unset in an env means "same host as the
// user feed" (what the test zone ran).
const renderedPriceFeedEndpoint =
  import.meta.env.VITE_APP_RENDERED_PRICE_FEED_ENDPOINT || streamEndpoint;
const error = ref({
  happens: false,
  code: '',
  title: '',
  detail: '',
});

/**
 * Socket Refs
 */
const priceUpdateSocket = ref();
const userFeedSocket = ref();
const userFeedSocketGo = ref(); // New streaming API user feed socket
const betConfirmationModal = ref();
const fillSummaryModal = ref();
const priceMovedModal = ref(null);
const slippageConfirmModal = ref();
const alert = ref({
  shown: false,
  type: 'ok',
  title: '',
  detail: '',
});

function pageHidden() {
  // Check if the document is hidden (tab not in focus)
  return document.hidden || document.visibilityState === 'hidden';
}

class ActivityTracker {
  static activityPollInterval = 1000;
  static serverUpdateInterval = 5 * 60 * 1000; // Production
  // static serverUpdateInterval = 5 * 1000;  // Testing
  interval = null;
  constructor() {
    this.lastPollTime = Date.now();
    this.start = Date.now();
    this.totalActiveDuration = 0;
    this.lastMouseMoveTime = Date.now();

    // Activity tracking
    this.interval = setInterval(() => {
      this.pollActivity();
    }, ActivityTracker.activityPollInterval);

    document.addEventListener('mousemove', () => {
      this.lastMouseMoveTime = Date.now();
    });
  }

  reset() {
    this.lastPollTime = Date.now();
    this.start = Date.now();
    this.totalActiveDuration = 0;
    this.lastMouseMoveTime = 0;
  }

  isActive() {
    // Definitely not active if the page is hidden
    if (pageHidden()) return false;

    // Active if they've moved the mouse in the last 30 seconds
    return this.lastMouseMoveTime > Date.now() - 30 * 1000;
  }

  hasBeenActive() {
    return (
      this.lastMouseMoveTime > Date.now() - ActivityTracker.serverUpdateInterval
    );
  }

  pollActivity() {
    const currentTime = Date.now();
    const pollGapDuration = currentTime - this.lastPollTime;
    this.lastPollTime = currentTime;

    if (this.isActive()) {
      if (pollGapDuration > ActivityTracker.activityPollInterval + 1500) {
        this.logActivity(ActivityTracker.activityPollInterval);
      } else {
        this.logActivity(pollGapDuration);
      }
    }
  }

  logActivity(duration) {
    this.totalActiveDuration += duration;
  }

  getDurations() {
    return {
      clockDuration: Date.now() - this.start,
      activeDuration: this.totalActiveDuration,
    };
  }
}

const globalActivityTracker = new ActivityTracker();
function logActiveUser() {
  if (userStore.user.isLoggedIn && globalActivityTracker.hasBeenActive()) {
    const { request } = useAPI('frontend-logging/activeUser/', true);
    const { clockDuration, activeDuration } =
      globalActivityTracker.getDurations();
    request({
      totalTime: clockDuration,
      activeTime: activeDuration,
    });
  }
}

setInterval(logActiveUser, ActivityTracker.serverUpdateInterval);

function initSocket(endpoint, type, query) {
  if (!authStore.token || !wsEndpoint) {
    return;
  }
  const socket = io(endpoint, {
    reconnection: true,
    reconnectionDelayMax: 10000,
    secure: true,
    transports: ['websocket', 'polling'],
    query,
  });

  const disconnectionActivityTracker = new ActivityTracker();

  const { request: logOngoingDisconnection } = useAPI(
    'frontend-logging/websocketOngoingDisconnection/',
    true,
  );
  function pollOngoingDisconnection() {
    if (!socket.disconnected) return;

    // Try to reconnect to help the user
    socket.connect();

    const { clockDuration, activeDuration } =
      disconnectionActivityTracker.getDurations();
    // Define ongoing disconnection as greater than 10 seconds
    if (clockDuration < 10 * 1000) return;

    logOngoingDisconnection({
      socketType: type,
      clockTimeDisconnected: clockDuration,
      activeTimeDisconnected: activeDuration,
      isActive: globalActivityTracker.hasBeenActive(),
    });
  }

  // Needs to use the same polling interval as the user activity so we can track relative to active users
  setInterval(pollOngoingDisconnection, ActivityTracker.serverUpdateInterval);

  let attempt = 0;
  const maxAttempts = 5;

  const tryReconnect = () => {
    if (attempt < maxAttempts) {
      setTimeout(
        () => {
          attempt += 1;
          // console.log(`Attempting to reconnect ${type}... (Attempt ${attempt})`);
          socket.connect();
          socket.once('connect', () => {
            // console.log(`${type} reconnected successfully.`);
            attempt = 0;
          });
        },
        2 ** attempt * 1000,
      );
    }
    // else {
    // console.log(`Max reconnection attempts reached for ${type}. Stopping reconnection attempts.`);
    // }
  };
  socket.on('connect', () => {
    miscStore.setSocketStatus({ type, data: socket.id });
    for (let index = 0; index < notificationStore.alerts.length; index += 1) {
      const element = notificationStore.alerts[index];
      if (element.title === 'Socket Disconnect') {
        notificationStore.removeAlert(element.id);
      }
    }
  });

  const { request: logDisconnection } = useAPI(
    'frontend-logging/websocketDisconnection/',
    true,
  );
  socket.on('disconnect', (reason, details) => {
    disconnectionActivityTracker.reset();
    let serializedDetails;
    try {
      serializedDetails = JSON.stringify(details);
    } catch (error) {
      serializedDetails = 'Error serializing details';
      console.error('Error serializin disconnection details', error);
    }
    logDisconnection({ socketType: type, reason, serializedDetails });

    miscStore.setSocketStatus({ type, data: '' });
    tryReconnect();
  });

  socket.io.on('reconnect', () => {
    const { request: logReconnection } = useAPI(
      'frontend-logging/websocketReconnection/',
      true,
    );
    const { clockDuration, activeDuration } =
      disconnectionActivityTracker.getDurations();
    logReconnection({
      socketType: type,
      clockTimeDisconnected: clockDuration,
      activeTimeDisconnected: activeDuration,
    });
  });

  socket.io.on('reconnect_error', (error) => {
    console.error(error);
  });

  return socket;
}

// user feed socket inclues positionUpdate, notification, seedError, banner message, updateOffer, depositConfirmed
function initUserFeedSocket() {
  if (!authStore.token || !wsEndpoint) {
    return;
  }
  userFeedSocket.value = initSocket(
    `${wsEndpoint}/user/${userStore.user.username}`,
    'userFeedSocket',
    { token: authStore.token },
  );
  if (userFeedSocket.value) {
    userFeedSocket.value.on('positionUpdate', (positionUpdate) => {
      const positionUpdateObject = JSON.parse(positionUpdate);
      handlePositionUpdate(positionUpdateObject);
    });

    // functionality on userfeedsocket notifications
    userFeedSocket.value.on('notification', (notification) => {
      const parsedNotification = JSON.parse(notification);
      let detail = parsedNotification.message ?? parsedNotification.detail;
      detail = detail.split('\n')[0];
      const mappedNotification = {
        status: parsedNotification.status || 'ok',
        title: parsedNotification.title,
        detail,
        type: parsedNotification.type,
        id: parsedNotification.id,
      };
      emitter.emit('showAlert', mappedNotification);
    });

    userFeedSocket.value.on('seedError', (msg) => {
      const parsedMsg = JSON.parse(msg);
      if (parsedMsg.type === 'seedError') {
        emitter.emit('showAlert', {
          shown: true,
          status: 'danger',
          title: `Error creating order`,
          detail: parsedMsg.detail,
        });
      } else {
        const { league, bookType } = JSON.parse(msg);
        emitter.emit('showAlert', {
          shown: true,
          status: 'danger',
          title: `Error fetching ${league.toUpperCase()} games from ${bookType}`,
          detail: '',
        });
      }
    });

    userFeedSocket.value.on('bannerMessage', (message) => {
      const parsedMessage = JSON.parse(message);
      if (parsedMessage?.length > 0) {
        // Add each banner from the array to the notification store
        parsedMessage.forEach((banner) => {
          notificationStore.addBanner(banner);
        });
      }
    });

    userFeedSocket.value.on('depositConfirmed', (text) => {
      emitter.emit('showAlert', {
        type: 'deposit-confirmed',
        shown: true,
        status: 'ok & update',
        title: 'Deposit Confirmed',
        detail: text,
      });
      emitter.emit('depositConfirmed');
    });

    requestNotifications();
  }
}

function initPriceUpdateSocket() {
  if (!userStore.user.isLoggedIn || !authStore.token || !wsEndpoint) {
    console.log('Not connecting to price update socket');
    return;
  }

  priceUpdateSocket.value = initSocket(
    `${wsEndpoint}/priceUpdates`,
    'priceUpdatesSocket',
    { token: authStore.token },
  );
  if (priceUpdateSocket.value) {
    priceUpdateSocket.value.on('otbUpdate', (gameMessage) => {
      const gameID = JSON.parse(gameMessage);
      if (gameID) {
        exchangeStore.removeGame({ id: gameID });
      }
    });
    priceUpdateSocket.value.on('gameUpdate', async (game) => {
      const gameObject = JSON.parse(game);
      if (gameObject) {
        exchangeStore.processGameUpdate(gameObject, route.params.sport);
      }
    });
    priceUpdateSocket.value.on('orderUpdate', (orderObjString) => {
      const orderObject = JSON.parse(orderObjString);
      // if route name is GamesPage of orderObject.live
      // emit spreadUpdate with OrderObject
      // do we need to do anything here
      placeTracker.observeOrderUpdate(orderObject);
      exchangeStore.processOrderUpdate(orderObject);
    });
  }
}

function logGoActiveUser() {
  if (userStore.user.isLoggedIn && globalActivityTracker.hasBeenActive()) {
    useAPI('frontend-logging/goActiveUser/', true).request();
  }
}

let priceUpdateSocketConnected = false;
let priceUpdateSocketDisconnectionTime = Date.now();

function pollPriceUpdateSocketOngoingDisconnection() {
  if (!userStore.user.isLoggedIn) return;
  if (priceUpdateSocketConnected) return;
  if (Date.now() - priceUpdateSocketDisconnectionTime < 10 * 1000) return;

  useAPI('frontend-logging/goWebsocketOngoingDisconnection/', true).request({
    socketType: 'priceUpdateSocket',
    isActive: globalActivityTracker.hasBeenActive(),
  });
}

let userFeedSocketConnected = false;
let userFeedSocketDisconnectionTime = Date.now();

function pollUserFeedSocketOngoingDisconnection() {
  if (!userStore.user.isLoggedIn) return;
  if (userFeedSocketConnected) return;
  if (Date.now() - userFeedSocketDisconnectionTime < 10 * 1000) return;

  useAPI('frontend-logging/goWebsocketOngoingDisconnection/', true).request({
    socketType: 'userFeedSocket',
    isActive: globalActivityTracker.hasBeenActive(),
  });
}

function initPriceUpdateSocketGo() {
  if (!streamEndpoint) {
    console.log(
      'Not connecting to Streaming API WebSocket: No stream endpoint',
    );
    return;
  }
  if (useRenderedPriceFeed) {
    initPriceUpdateSocketJSON();
  } else {
    initPriceUpdateSocketProtobuf();
  }
}

function initPriceUpdateSocketProtobuf() {
  console.log('Connecting to Streaming API WebSocket (Protobuf)');
  let url = '';
  if (!userStore.user.isLoggedIn) {
    url = `${streamEndpoint}/price-stream-public-pb`;
  } else {
    url = `${streamEndpoint}/price-stream-pb?token=${authStore.token}&platform=web`;
  }

  let connectedOnce = false;

  priceUpdateSocket.value = new ProtobufWebSocketManager(url, {
    onmessage: (decoded) => {
      switch (decoded.type) {
        case 'orderUpdate':
          // Tap before view-based filtering so telemetry sees true delivery.
          placeTracker.observeOrderUpdate(decoded.payload);
          exchangeStore.processOrderUpdate(decoded.payload);
          break;

        case 'gameUpdate':
          exchangeStore.processGameUpdate(decoded.payload, route.params.sport);
          break;

        case 'matchedVolumeUpdate':
          exchangeStore.processMatchedVolumeUpdate(decoded.payload);
          break;

        default:
          console.warn('Unknown message type:', decoded.type);
      }
    },
    onopen: () => {
      console.log('Connected to Streaming API WebSocket (Protobuf)');
      priceUpdateSocketConnected = true;
      connectedOnce = true;

      // Keep socket status real on this path — telemetry ships it with every event.
      miscStore.setSocketStatus({
        type: 'priceUpdateSocket',
        data: 'connected-pb',
      });

      // Re-attach the subscription manager so the current desired set is
      // replayed on the fresh socket. No-op when the feature flag is off.
      //
      // We deliberately do NOT call applyRouteSubscription(route) here:
      // - The router.afterEach hook already keeps the desired set in sync
      //   with route changes regardless of socket state.
      // - For HomePage and `featured-*` routes the desired set is owned by
      //   the view (via subscriptionManager.setFeatured once REST resolves),
      //   so re-deriving from the route on reconnect would clear those
      //   featured sets and silently drop price updates until the view next
      //   refetched.
      subscriptionManager.attach(priceUpdateSocket.value);

      if (userStore.user.isLoggedIn) {
        useAPI('frontend-logging/goWebsocketConnection/', true).request({
          socketType: 'priceUpdateSocketPB',
        });
      }
    },
    onerror: (err) => {
      console.info('WebSocket error (Protobuf):', err);
    },
    onclose: (event) => {
      if (connectedOnce) {
        if (userStore.user.isLoggedIn) {
          useAPI('frontend-logging/goWebsocketDisconnection/', true).request({
            socketType: 'priceUpdateSocketPB',
          });
        }
      }
      console.info(
        'Streaming API WebSocket (Protobuf) closed:',
        event.reason || '(no reason)',
      );

      priceUpdateSocketConnected = false;
      priceUpdateSocketDisconnectionTime = Date.now();
      connectedOnce = false;
      miscStore.setSocketStatus({ type: 'priceUpdateSocket', data: '' });
    },
  });
}
// Renderer tells the client its feed may be stale (gap detected, generation
// rollover, etc.). The rendered feed carries no replay, so the only correct
// response is to refetch authoritative state via the existing REST paths.
function handlePriceFeedResyncRequired(payload) {
  const gameID = payload?.gameID;
  console.info(
    'priceFeedResyncRequired:',
    payload?.reason || '(no reason)',
    gameID || '(no game)',
  );

  // If the affected game is the one currently open, refetch its full book.
  const singleGameID = exchangeStore.singleGame?.game?.id;
  if (singleGameID && (!gameID || singleGameID === gameID)) {
    emitter.emit('fetchSingleGameData', singleGameID);
  }

  // Always refresh the broad sport/league data so list views recover too.
  // force: this runs after a socket drop specifically to re-sync data that
  // may have gone stale while we were disconnected, so it must bypass the
  // shared fetch's TTL rather than be served the pre-disconnect response.
  if (route.params.sport) {
    exchangeStore.getSportsData(route.params.sport, { force: true });
  }

  // The rendered feed has no replay, so list views (exchange, sportsbook,
  // homepage) must refetch their orderbooks or they keep pre-disconnect
  // prices. Each mounted list view listens for this and reuses its own
  // fetch.
  emitter.emit('priceFeedResync');
}

/////NOT USER AT THE MOMENT
function initPriceUpdateSocketJSON() {
  console.log('Connecting to Streaming API WebSocket (JSON)');
  let url = '';
  if (!userStore.user.isLoggedIn) {
    url = `${renderedPriceFeedEndpoint}/price-stream-public`;
  } else {
    url = `${renderedPriceFeedEndpoint}/price-stream?token=${authStore.token}&platform=web`;
  }
  let connectedOnce = false;
  // Unlike connectedOnce (reset on every close for disconnection logging),
  // this survives closes so onopen can tell a reconnect from the first
  // connect of the session.
  let hasConnectedBefore = false;

  priceUpdateSocket.value = new WebSocketManager(url, {
    onmessage: (event) => {
      try {
        const [type, payload] = JSON.parse(event.data);

        switch (type) {
          case 'orderUpdate':
            placeTracker.observeOrderUpdate(payload);
            exchangeStore.processOrderUpdate(payload);
            break;

          case 'gameUpdate':
            exchangeStore.processGameUpdate(payload, route.params.sport);
            break;

          case 'matchedVolumeUpdate':
            exchangeStore.processMatchedVolumeUpdate(payload);
            break;

          case 'priceFeedResyncRequired':
            handlePriceFeedResyncRequired(payload);
            break;

          default:
            console.warn('Unknown message type:', type);
        }
      } catch (err) {
        console.error('Error parsing message from Streaming API:', err);
      }
    },
    onopen: () => {
      console.log('Connected to Streaming API WebSocket (JSON)');
      const isReconnect = hasConnectedBefore;
      hasConnectedBefore = true;
      priceUpdateSocketConnected = true;
      connectedOnce = true;
      miscStore.setSocketStatus({
        type: 'priceUpdateSocket',
        data: 'connected-json',
      });

      // Narrow this connection to what the current page wants. The fanout
      // registers every new connection with a default all-games
      // subscription (the full firehose); attaching the manager here replays
      // the page's desired set over the JSON subscribe API on every open,
      // including reconnects. No-op when the feature flag is off. The
      // router.afterEach hook and the views keep the desired set current
      // regardless of socket state (see applyRouteSubscription).
      subscriptionManager.attachJSON(priceUpdateSocket.value);

      // The rendered feed carries no replay, and server-side continuity
      // tracking is fanout-wide: it cannot know what THIS client missed
      // while disconnected. Without a forced resync, the UI keeps showing
      // pre-disconnect prices until each market's next natural update.
      if (isReconnect) {
        handlePriceFeedResyncRequired({ reason: 'reconnect' });
        // The resync refetches this view's book; also rebuild the full
        // own-orders record so ownership stamps on every game are right.
        if (userStore.user.isLoggedIn) {
          exchangeStore.reseedUserOrders();
        }

        // Also ask the feed itself for a fresh snapshot of the open game so
        // the live book repaints immediately, not just after the REST
        // refetch lands.
        const singleGameID = exchangeStore.singleGame?.game?.id;
        if (singleGameID) {
          try {
            priceUpdateSocket.value?.send(
              JSON.stringify([
                'priceFeedSnapshotRequest',
                { gameID: singleGameID, reason: 'reconnect' },
              ]),
            );
          } catch (err) {
            console.info('Snapshot request after reconnect failed:', err);
          }
        }
      }

      if (userStore.user.isLoggedIn) {
        useAPI('frontend-logging/goWebsocketConnection/', true).request({
          socketType: 'priceUpdateSocket',
        });
      }
    },
    onerror: (err) => {
      console.info('WebSocket error:', err);
    },
    onclose: (event) => {
      if (connectedOnce && userStore.user.isLoggedIn) {
        useAPI('frontend-logging/goWebsocketDisconnection/', true).request({
          socketType: 'priceUpdateSocket',
        });
      }
      console.info(
        'Streaming API WebSocket closed:',
        event.reason || '(no reason)',
      );

      priceUpdateSocketConnected = false;
      priceUpdateSocketDisconnectionTime = Date.now();
      connectedOnce = false;
      miscStore.setSocketStatus({ type: 'priceUpdateSocket', data: '' });
    },
  });
}

function closePriceUpdateSocketGo() {
  if (priceUpdateSocket.value) {
    try {
      priceUpdateSocket.value.close();
    } catch (e) {
      console.error('Error closing priceUpdateSocket:', e);
    } finally {
      priceUpdateSocket.value = null;
    }
  }
}

function initUserFeedSocketGo() {
  if (!userStore.user.isLoggedIn || !authStore.token || !streamEndpoint) {
    console.log(
      'Not connecting to User Feed Streaming API WebSocket: Not logged in or no token',
    );
    return;
  }
  console.log('Connecting to User Feed Streaming API WebSocket');

  const url = `${streamEndpoint}/v1/user?token=${authStore.token}&platform=web`;

  let connectedOnce = false;
  // Counts opens across this manager's lifetime; unlike connectedOnce it is
  // not reset on close, so it distinguishes a reconnect from the first open.
  let userFeedOpenCount = 0;

  userFeedSocketGo.value = new WebSocketManager(url, {
    onmessage: (event) => {
      try {
        handleUserFeedV1SocketMessage(event.data);
      } catch (err) {
        console.error(
          'Error handling message from User Feed Streaming API:',
          err,
        );
      }
    },
    onopen: () => {
      console.log('Connected to User Feed Streaming API WebSocket');
      const isReconnect = userFeedOpenCount > 0;
      userFeedOpenCount += 1;
      userFeedSocketConnected = true;
      connectedOnce = true;
      miscStore.setSocketStatus({
        type: 'userFeedSocket',
        data: 'connected-go',
      });

      useAPI('frontend-logging/goWebsocketConnection/', true).request({
        socketType: 'userFeedSocket',
      });
      // Refresh the user, no need to await
      userStore.getMe();

      // Position-updates are live-only (no replay), so any updates emitted
      // while this socket was down are gone forever. Refetch the open game's
      // book: its HTTP order summary reseeds userOrders in the store, which
      // restores aggregate ownership (green highlight / own-volume) that
      // would otherwise be unrecoverable until the user's next order event.
      if (isReconnect) {
        const singleGameID = exchangeStore.singleGame?.game?.id;
        if (singleGameID) {
          emitter.emit('fetchSingleGameData', singleGameID);
        }
        // The single-game refetch only covers the open game; list views and
        // the lobby need the full own-orders record rebuilt too.
        exchangeStore.reseedUserOrders();
      }
    },
    onerror: (err) => {
      console.info('User Feed WebSocket error:', err);
    },
    onclose: (event) => {
      if (connectedOnce) {
        useAPI('frontend-logging/goWebsocketDisconnection/', true).request({
          socketType: 'userFeedSocket',
        });
      }
      console.info(
        'User Feed Streaming API WebSocket closed:',
        event.reason || '(no reason)',
      );

      userFeedSocketConnected = false;
      userFeedSocketDisconnectionTime = Date.now();
      connectedOnce = false;
      miscStore.setSocketStatus({ type: 'userFeedSocket', data: '' });
    },
  });
}

function closeUserFeedSocketGo() {
  if (userFeedSocketGo.value) {
    try {
      userFeedSocketGo.value.close();
    } catch (e) {
      console.error('Error closing Go User Feed Socket:', e);
    } finally {
      userFeedSocketGo.value = null;
    }
  }
}

const useGoUserFeedSocket = true;
const useGoPriceUpdateSocket =
  import.meta.env.VITE_USE_GO_PRICE_UPDATE_SOCKET === 'true';
const useRenderedPriceFeed =
  import.meta.env.VITE_USE_RENDERED_PRICE_FEED === 'true';
console.log('useGoUserFeedSocket', useGoUserFeedSocket);
console.log('useGoPriceUpdateSocket', useGoPriceUpdateSocket);
console.log('useRenderedPriceFeed', useRenderedPriceFeed);

function setupGoTracking() {
  setInterval(logGoActiveUser, ActivityTracker.serverUpdateInterval);
  if (useGoUserFeedSocket) {
    setInterval(
      pollUserFeedSocketOngoingDisconnection,
      ActivityTracker.serverUpdateInterval,
    );
  }
  if (useGoPriceUpdateSocket) {
    setInterval(
      pollPriceUpdateSocketOngoingDisconnection,
      ActivityTracker.serverUpdateInterval,
    );
  }
}

function openSockets() {
  closeSockets();
  if (authStore.token) {
    if (useGoUserFeedSocket || useGoPriceUpdateSocket) {
      setupGoTracking();
    }
    if (useGoUserFeedSocket) {
      initUserFeedSocketGo();
    } else {
      initUserFeedSocket();
    }
  }
  if (useGoPriceUpdateSocket) {
    initPriceUpdateSocketGo();
  } else {
    initPriceUpdateSocket();
  }
  // Rebuild the client's record of its own open orders whenever the sockets
  // are (re)opened: page load and login. Page-scoped order summaries only
  // cover one page's games, so without this the record starts empty on Home
  // and props pages and stays partial elsewhere (no-op when logged out).
  if (authStore.token) {
    exchangeStore.reseedUserOrders();
  }
}

// A tab coming back from the background may have missed position-updates
// (live-only, no replay) for however long it slept; rebuild from the server.
function onVisibilityChangeReseedUserOrders() {
  if (document.visibilityState === 'visible' && authStore.token) {
    exchangeStore.reseedUserOrders();
  }
}

function closePriceUpdateSocket() {
  if (priceUpdateSocket.value) {
    priceUpdateSocket.value.close();
    priceUpdateSocket.value.removeAllListeners();
    priceUpdateSocket.value = null;
  }
}

function closeUserFeedSocket() {
  if (userFeedSocket.value) {
    userFeedSocket.value.close();
    userFeedSocket.value.removeAllListeners();
    userFeedSocket.value = null;
  }
}

function closeSockets() {
  if (useGoPriceUpdateSocket) {
    closePriceUpdateSocketGo();
  } else {
    closePriceUpdateSocket();
  }
  if (useGoUserFeedSocket) {
    closeUserFeedSocketGo();
  } else {
    closeUserFeedSocket();
  }
}

const timer = ref(null);

openSockets();

const isMobile = computed(() => {
  return screenWidth.value <= 576;
});

watch(isMobile, () => {
  if(isMobile.value){
    window.Intercom('update', { hide_default_launcher: true });
  } else {
    window.Intercom('update', { hide_default_launcher: false });
  }
});

// Best Offers data is no longer fetched here — ChatActivity fetches it
// (exchangeStore.fetchBestOffers) when the Best Offers panel is opened,
// since that's the only place in the app that renders it.

// Routed through the store's shared fetch so app boot and the post-login
// refresh (which the store also listens for) collapse into one request
// instead of firing this endpoint concurrently from two places.
async function getSports() {
  await exchangeStore.fetchSportLeagues();
  exchangeStore.applyLeaguesForSport(route.params?.sport);
}

function requestNotifications() {
  if (userFeedSocket.value) {
    userFeedSocket.value.emit('request_notifications');
  }
}

getSports();


emitter.on('signed-up', () => {
  openSockets();
});

function isPriorityAlert(payload) {
  const matchNotificationTypes = [
    'orderFill',
    'new-player',
    'deposit-confirmed',
  ];
  return matchNotificationTypes.includes(payload.type);
}

function notificationRequiresGetMe(payload) {
  // Most of this has been refactored out, but balance update still needs to be factored out
  const notificationTypes = ['balance-update'];
  return notificationTypes.includes(payload.type);
}

function notificationShouldTimeout(payload) {
  // This is correct as of 2025-10-03
  const excludeTimeoutTypes = [
    'orderFill',
    'new-player',
    'deposit-confirmed',
    'socket-disconnected',
  ];
  if (excludeTimeoutTypes.includes(payload.type)) {
    return false;
  }
  return true;
}
emitter.on('openOffersForGame', (gameID) => {
  if (!gameID) return;
  globalOpenOffersGameID.value = gameID;
  showGlobalOpenOffers.value = true;
});

emitter.on('showAlert', async (payload) => {
  if (!payload.id) {
    payload.id = uuidv4();
  }
  // Going to totally refactor this
  // Need to think through how to split up responsibilities etc
  if (isPriorityAlert(payload)) {
    notificationStore.addPriorityAlert(payload);
  } else {
    notificationStore.addAlert(payload);
  }
  // Going to take this out of the showAlert function
  if (notificationRequiresGetMe(payload)) {
    userStore.getMe();
  }
  // This is good. Want some notifications to time out
  if (notificationShouldTimeout(payload)) {
    if (payload.type === 'password-unsafe') {
      return;
    }
    timerRemoveNotificationById.value = setTimeout(() => {
      notificationStore.removeAlert(payload.id);
    }, 4200);
  }
});
emitter.on('getActivityFeedMessages', (payload) => {
  if (payload && payload.page) {
    getActivityFeedMessages(payload);
  }
});
emitter.on('ApplicationError', () => {
  error.value = {
    happens: true,
    code: '500',
    title: 'Internal Error',
    detail: 'Something went wrong. Try and reload the page.',
  };
});
emitter.on('NetworkError', () => {
  error.value = {
    happens: true,
    code: 'XXX',
    title: 'Network Error',
    detail: 'Oops, something went wrong. Please try again',
  };
});
emitter.on('logged-in', () => {
  openSockets();
  if (userStore.user.id) {
    getSports();
    notificationStore.refreshBanners();
    notificationStore.refreshInboxNotifications();
  }
  SentryUtils.setUserInfo({
    userId: userStore.user.id,
    username: userStore.user.username,
  });
  syncTwoFactorMethod();
});
emitter.on('userLogOut', async () => {
  resetAllStores();
  closeSockets();
  SentryUtils.unsetUserInfo();

  if (route.path !== '/log-in' && route.path !== '/') {
    router.push({
      path: '/log-in',
      query: { redirect: route.fullPath },
    });
  }
});

emitter.on('appWorkerUpdates', (payload) => {
  if (payload && payload.type === 'UPDATE_GAMES') {
    setGameExpiry(payload.view);
  } else if (payload && payload.type === 'REMOVE_GAMES') {
    cancelGamesExpiry(payload.gameIds);
  }
});

emitter.on('sendActivityFeedMessage', (payload) => {
  if (payload && payload.message) {
    sendActivityFeedMessage({
      message: payload.message,
      senderId: userStore.user.id,
      senderUsername: userStore.user.username,
      type: 'USER_MESSAGE',
      sentAt: new Date(),
    });
  }
});

emitter.on('getActivityFeedMessages', (payload) => {
  if (payload && payload.page) {
    getActivityFeedMessages(payload);
  }
});

emitter.on('pinActivityFeedMessage', (payload) => {
  if (payload && payload.messageId) {
    pinActivityFeedMessage(payload);
  }
});

emitter.on('unpinActivityFeedMessage', (payload) => {
  if (payload && payload.messageId) {
    unpinActivityFeedMessage(payload);
  }
});

emitter.on('disableError', (payload) => {
  if (payload) {
    error.value.happen = payload.error;
  }
});

const authTokenChannel = new BroadcastChannel('auth-token');

function updateToken(newToken, broadcast) {
  closeSockets();
  authStore.updateToken(newToken);
  if (broadcast) {
    authTokenChannel.postMessage(newToken);
  }
  if (newToken) {
    openSockets();
  }
}

authTokenChannel.onmessage = (event) => {
  const newToken = event.data;
  updateToken(newToken, false);
};

emitter.on('updateAuthToken', (newToken) => {
  updateToken(newToken, true);
});

// Handle bet confirmation modal
emitter.on('showBetConfirmationModal', (betData) => {
  if (betConfirmationModal.value) {
    betConfirmationModal.value.show(betData);
  }
});

// Handle fill summary modal (partial fill / odds differed from projection)
emitter.on('showFillSummaryModal', (fillData) => {
  if (fillSummaryModal.value) {
    fillSummaryModal.value.show(fillData);
  }
});

emitter.on('showSlippageConfirmModal', (slippageData) => {
  if (slippageConfirmModal.value) {
    slippageConfirmModal.value.show(slippageData);
  }
});

// Live re-book offer after an engine no-fill (PRO-234).
emitter.on('showPriceMovedModal', (priceMovedData) => {
  if (priceMovedModal.value) {
    priceMovedModal.value.show(priceMovedData);
  } else {
    // No mounted modal to show — decline instead of dropping the event, or
    // the awaiting take flow never settles and the slip stays frozen in
    // loading (bugbot). Tagged so the slip reports it as a failure, not a
    // user decline (spec-review 20 M3).
    priceMovedData?.onCancel?.('no_modal_mounted');
  }
});
// The owning slip unmounted mid-prompt — close the prompt so it can't book
// from dead closure state (spec-review 20 M5).
emitter.on('hidePriceMovedModal', () => {
  priceMovedModal.value?.dismiss();
});

// Handle deposit modal — gate alpha users through KYC first.
// Closed accounts get logged out immediately (matches backend enforcement).
emitter.on('showDepositModal', () => {
    if (userStore.user.closed) {
        emitter.emit('userLogOut');
        return;
    }
    // Cashier-blocked users (blocked affiliate codes / usernames / demo) have no
    // wallet access — never open the deposit/withdraw modal for them, no matter
    // which entry point fired. Single source of truth, so nothing can drift.
    if (!userCanUseCashier(userStore.user)) {
        return;
    }
    if (userStore.user.isAlphaUser) {
        showPersonalInfoModal.value = true;
        return;
    }
    showDepositModal.value = !showDepositModal.value;
});

function onPersonalInfoCompleted() {
    showPersonalInfoModal.value = false;
    showDepositModal.value = true;
}

// Handle withdraw modal — gate KYC-required users through Veriff first, the same
// way showDepositModal and the cashier withdraw action do. Without this, a user
// with veriffData.required and an unapproved status could open withdraw directly
// (account page button, etc.) and bypass verification.
emitter.on('showWithdrawModal', () => {
    if (!userCanUseCashier(userStore.user)) {
        return;
    }
    const veriffData = userStore.user?.veriffData;
    if (veriffData?.required === true && veriffData?.status !== 'approved') {
        PostHogUtils.markKycRequired();
        emitter.emit('showVeriffModal');
        return;
    }
    showWithdrawModal.value = !showWithdrawModal.value;
});

// Deterministic wallet tab-switch (segmented Deposit | Withdraw toggle inside the
// modals). Unlike the toggle events above, these explicitly open one and close
// the other so switching can't accidentally close both — and they don't re-run
// the deposit KYC/alpha gate (the user is already inside the wallet flow).
emitter.on('walletSwitchToWithdraw', () => {
    // Defense in depth: even inside the wallet flow, never expose withdraw to a
    // KYC-required, unapproved user. Divert to Veriff and close the deposit modal.
    const veriffData = userStore.user?.veriffData;
    if (veriffData?.required === true && veriffData?.status !== 'approved') {
        showDepositModal.value = false;
        PostHogUtils.markKycRequired();
        emitter.emit('showVeriffModal');
        return;
    }
    showDepositModal.value = false;
    showWithdrawModal.value = true;
});
emitter.on('walletSwitchToDeposit', () => {
    showWithdrawModal.value = false;
    showDepositModal.value = true;
});

// Offer 2FA enrollment right after a deposit credits (factor-less users only).
// Listen on the emitter, NOT a specific socket handler — both the legacy and
// the Go user-feed paths emit `depositConfirmed`, and the Go path is the one
// active in prod (useGoUserFeedSocket = true). Hooking a single socket handler
// would silently miss the active path.
emitter.on('depositConfirmed', () => {
    promptTwoFactorEnrollAfterDeposit();
});

// Open the 2FA enrollment/management flow from anywhere (the cashier 2FA
// info banner, the account settings, etc.).
emitter.on('showTwoFactorEnroll', (payload) => {
    twoFactorEnrollSource.value = payload?.source || 'account';
    showTwoFactorEnroll.value = true;
});

emitter.on('showLoginModal', () => {
  showLoginModal.value = true;
});

emitter.on('showVeriffModal', async (sessionID) => {
    showVeriffModal.value = !showVeriffModal.value;
    if (showVeriffModal.value === false && sessionID && userStore.user?.veriffData?.['status'] !== 'approved') {
      await userStore.verifyUserKYC(sessionID);
    }
});

timeOutOldNotifs();

onBeforeUnmount(() => {
  document.removeEventListener(
    'visibilitychange',
    onVisibilityChangeReseedUserOrders,
  );
  closeSockets();
  terminateAppWorker();
  clearTimeout(timerRemoveNotificationById.value);
  clearTimeout(timersetGameOddsDelta.value);
  clearTimeout(timer.value);
  timerRemoveNotificationById.value = null;
  emitter.off('logged-in');
  emitter.off('signed-up');
  emitter.off('openArbOfferInfoModal', openArbOfferInfo);
  emitter.off('openSlippageInfoModal', openSlippageInfo);
  emitter.off('showAlert');
  emitter.off('getActivityFeedMessages');
  emitter.off('ApplicationError');
  emitter.off('NetworkError');
  emitter.off('userLogOut');
  emitter.off('sendActivityFeedMessage');
  emitter.off('pinActivityFeedMessage');
  emitter.off('unpinActivityFeedMessage');
  emitter.off('disableError');
  emitter.off('appWorkerUpdates');
  emitter.off('positionUpdate');
  emitter.off('depositConfirmed');
  emitter.off('showBetConfirmationModal');
  emitter.off('showFillSummaryModal');
  emitter.off('showSlippageConfirmModal');
  emitter.off('showPriceMovedModal');
  emitter.off('hidePriceMovedModal');
  emitter.off('showDepositModal');
  emitter.off('showWithdrawModal');
  emitter.off('walletSwitchToWithdraw');
  emitter.off('walletSwitchToDeposit');
  emitter.off('showTwoFactorEnroll');
  emitter.off('showVeriffModal');
  emitter.off('showLoginModal');
  emitter.off('openOffersForGame');
  showPersonalInfoModal.value = false;
  if (gameExpiryWorker) {
    gameExpiryWorker.onmessage = null;
  }
});

function closeBanner(bannerId) {
  notificationStore.acknowledgeAndRemoveBanner(bannerId);
}

const arbOfferInfoModalOpen = ref(false);
// Allow any component (e.g. the Arb Offer toggle inside Create Offer) to
// surface the explainer modal on demand via the global emitter.
function openArbOfferInfo() {
  arbOfferInfoModalOpen.value = true;
}
emitter.on('openArbOfferInfoModal', openArbOfferInfo);

const slippageInfoModalOpen = ref(false);
// Same pattern for the live-slippage explainer (Learn more on the slip card).
function openSlippageInfo() {
  slippageInfoModalOpen.value = true;
}
emitter.on('openSlippageInfoModal', openSlippageInfo);
function timeOutOldNotifs() {
  if (
    Array.isArray(notificationStore.alerts) &&
    notificationStore.alerts.length > 0
  ) {
    notificationStore.alerts.forEach((payload) => {
      if (
        payload.type !== 'orderFill' &&
        payload.type !== 'new-player' &&
        payload.type !== 'deposit-confirmed' &&
        payload.type !== 'socket-disconnected'
      ) {
        timerRemoveNotificationById.value = setTimeout(() => {
          notificationStore.removeAlert(payload.id);
        }, 4000);
      }
    });
  }
}

function showAlert(data) {
  clearTimeout(timer.value);
  alert.value = data;
  timer.value = setTimeout(() => {
    alert.value.shown = false;
    error.value.happens = false;
  }, 6000);
}

function nextPriorityAlert(notificationId) {
  notificationStore.removePriorityAlert(notificationId);
}

function clearPriorityAlerts() {
  notificationStore.clearPriorityAlerts();
}

function removeAlert(notificationId) {
  notificationStore.removeAlert(notificationId);
}
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '@/scss/theme/variables.scss' as theme;

.rebate-accent-bar {
  background: linear-gradient(90deg, #1a3a2a 0%, #2e5a3a 50%, #1a4a2a 100%);
  text-align: center;
  padding: 6px 16px;
  font-size: 0.8rem;

  &__link {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    text-decoration: none;
    color: rgba(255, 255, 255, 0.85);
    transition: color 0.2s ease;

    &:hover {
      color: #ffffff;
    }
  }

  &__badge {
    background: rgba(102, 187, 106, 0.25);
    border: 1px solid rgba(102, 187, 106, 0.4);
    border-radius: 4px;
    padding: 1px 8px;
    font-weight: 700;
    font-size: 0.7rem;
    letter-spacing: 0.5px;
    color: #66bb6a;
  }

  &__text {
    font-weight: 500;
    letter-spacing: 0.2px;
  }
}

.notifications-wrapper {
  position: fixed;
  right: 1rem;
  top: 1rem;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-end;

  @media screen and (max-width: map.get(theme.$grid-breakpoints, md)) {
    right: 1rem;
    top: 1rem;
    width: auto;
  }
  @media screen and (max-width: map.get(theme.$grid-breakpoints, sm)) {
    right: 0.5rem;
    top: 0.5rem;
  }
}
</style>
